import { GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Popover } from 'antd'
import dayjs from 'dayjs'
import { startCase } from 'lodash'
import moment from 'moment'

type OutageInfo = NonNullable<Gravitate.SupplyOption['outage_info']>

export type SupplyOutagePopoverProps = {
  outageType: OutageInfo['alert']
  outages: OutageInfo['outages']
}

const formatOutageTimespan = (value: OutageInfo['outages'][0]) => {
  let formatter = 'MM/DD/YYYY'
  if (value.schedule_local.schedule_type === 'event') {
    formatter += ', h:mm A'
  }
  const start = dayjs.tz(value.schedule_local.start_date, value.timezone ?? 'GMT').format(formatter)
  const end = value.schedule_local.end_date
    ? dayjs.tz(value.schedule_local.end_date, value.timezone ?? 'GMT').format(formatter)
    : 'Forever'

  return `${start} - ${end}`
}

export function SupplyOutagePopover({ outageType, outages }: SupplyOutagePopoverProps) {
  return (
    <Popover
      placement='bottomLeft'
      title={
        <Vertical>
          <Texto category='h5'>
            <Horizontal gap={8} alignItems='center'>
              <MaterialIcon
                weight={700}
                icon='money_off'
                style={{
                  color: outageType === 'error' ? 'var(--theme-error)' : 'var(--theme-optimal)',
                }}
              />{' '}
              {`${outageType === 'error' ? 'Outage Info' : 'Potential Outage Info'}`}
            </Horizontal>
          </Texto>
          <Texto category='label'>
            All times are displayed in the terminal's local timezone (
            {dayjs()
              .tz(outages[0].timezone ?? 'GMT')
              .format('z')}
            )
          </Texto>
        </Vertical>
      }
      content={
        <Vertical
          style={{ minWidth: 500, maxWidth: 650, maxHeight: 400, overflowY: 'scroll' }}
          gap={16}
          className='mt-2 p-1'
        >
          {outages.map((o) => {
            return (
              <div key={o.id} className='pr-2'>
                <Horizontal justifyContent='space-between' alignItems='flex-start' gap={8} className='mb-2'>
                  <Vertical>
                    <Texto weight='bold'>
                      {o.reason_code} ({startCase(o.schedule_utc?.schedule_type)})
                    </Texto>
                    <Texto category='label' weight={600}>
                      {formatOutageTimespan(o)}
                    </Texto>
                    {o.schedule_local.schedule_type === 'recurring' && (
                      <Horizontal style={{ flexWrap: 'wrap', gap: '0 8px' }}>
                        {o.schedule_local.days.map((day, index, self) => (
                          <Texto key={day.day_of_week} style={{ fontSize: '10px' }}>
                            {startCase(day.day_of_week).slice(0, 3)}.{' '}
                            {moment(day.start_time, 'HH:mm').format('hh:mm A')} -{' '}
                            {moment(day.end_time, 'HH:mm').format('hh:mm A')}
                            {index < self.length - 1 && ','}
                          </Texto>
                        ))}
                      </Horizontal>
                    )}
                  </Vertical>
                  {o.is_bulk && (
                    <Texto weight='bold' appearance='medium'>
                      Bulk Outage
                    </Texto>
                  )}
                </Horizontal>
                <Texto style={{ color: '#696969' }}>{o.notes}</Texto>
              </div>
            )
          })}
        </Vertical>
      }
    >
      <GraviButton
        appearance='outlined'
        style={{
          color: outageType === 'warning' ? 'var(--theme-warning)' : undefined,
          borderColor: outageType === 'warning' ? 'var(--theme-warning)' : undefined,
          backgroundColor: outageType === 'error' ? 'var(--theme-error-dim)' : 'var(--theme-warning-dim)',
        }}
        error={outageType === 'error'}
      >
        {outageType === 'error' ? 'Supply Outage' : 'Potential Supply Outage'}
      </GraviButton>
    </Popover>
  )
}
